import React, { useContext, useRef, useState } from "react";
import { Box, Popover, Slider, Switch, ToggleButton, ToggleButtonGroup, Typography } from "@mui/material";

import { getDrawToolPreferences, saveDrawToolPreferences } from "fond/draw/drawToolPreferences";
import { MagnetIcon } from "fond/svg_icons";

import { MapContext } from "../MapProvider";

import ToggleOptionButton from "./ToggleOptionButton";
import { IButtonProps } from "./Toolbar";
import ToolbarToggleButton from "./ToolbarToggleButton";

const vertexPriorityMarks = [
  { value: 0.000025, label: "Edge" },
  { value: 0.0025, label: "Balanced" },
  { value: 1.25, label: "Vertex" },
];

const SnappingButton: React.FC<IButtonProps> = ({ icon, title, ...rest }) => {
  const { drawControl, map } = useContext(MapContext);
  const [snap, setSnap] = useState(drawControl.current?.options?.snap || false);
  const [optionsAnchor, setOptionsAnchor] = useState<HTMLElement | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  // Local state for snap options — initialised from persisted preferences
  const [persisted] = useState(() => getDrawToolPreferences().snapOptions);
  const [snapPx, setSnapPx] = useState(persisted.snapPx);
  const [snapPointPx, setSnapPointPx] = useState(persisted.snapPointPx);
  const [snapToMidPoints, setSnapToMidPoints] = useState(persisted.snapToMidPoints);
  const [snapVertexPriorityDistance, setSnapVertexPriorityDistance] = useState(persisted.snapVertexPriorityDistance);

  const handleOnChange = () => {
    const currentValue = !!drawControl.current?.options?.snap;
    const newValue = !currentValue;

    setSnap(newValue);
    map?.fire("draw.options_changed", { snap: newValue });
  };

  const handleOptionsClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    setOptionsAnchor(optionsAnchor ? null : containerRef.current);
  };

  const handleCloseOptions = () => {
    setOptionsAnchor(null);
  };

  const fireSnapOptions = (patch: Record<string, unknown>) => {
    const current = drawControl.current?.options?.snapOptions ?? {};
    map?.fire("draw.options_changed", {
      snapOptions: { ...current, ...patch },
    });
    saveDrawToolPreferences({ snapOptions: patch });
  };

  return (
    <Box ref={containerRef} sx={{ display: "flex", flexDirection: "column" }}>
      <ToolbarToggleButton
        title={title || `${snap ? "Disable" : "Enable"} snapping`}
        {...rest}
        value="snap"
        onChange={handleOnChange}
        selected={snap}
        hasOptions
        data-testid="toolbar-snap-button"
      >
        {icon || <MagnetIcon />}
      </ToolbarToggleButton>

      <ToggleOptionButton onClick={handleOptionsClick} active={!!optionsAnchor} selected={snap} title="Snap options" />

      <Popover
        open={!!optionsAnchor}
        anchorEl={optionsAnchor}
        onClose={handleCloseOptions}
        anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
        transformOrigin={{ vertical: "top", horizontal: "center" }}
        slotProps={{
          paper: {
            sx: { p: 2, width: 260 },
          },
        }}
      >
        <Typography variant="body2" fontWeight={600} sx={{ mb: 1.5 }}>
          Snap Options
        </Typography>

        {/* snapPx */}
        <Typography variant="caption" color="text.secondary">
          Snap distance (px)
        </Typography>
        <Slider
          size="small"
          value={snapPx}
          min={1}
          max={50}
          step={1}
          valueLabelDisplay="auto"
          onChange={(_, v) => {
            const val = v as number;
            setSnapPx(val);
            fireSnapOptions({ snapPx: val });
          }}
        />

        {/* snapPointPx */}
        <Typography variant="caption" color="text.secondary">
          Point snap distance (px)
        </Typography>
        <Slider
          size="small"
          value={snapPointPx ?? snapPx}
          min={1}
          max={50}
          step={1}
          valueLabelDisplay="auto"
          onChange={(_, v) => {
            const val = v as number;
            setSnapPointPx(val);
            fireSnapOptions({ snapPointPx: val });
          }}
        />

        {/* snapVertexPriorityDistance */}
        <Typography variant="caption" color="text.secondary">
          Vertex priority
        </Typography>
        <ToggleButtonGroup
          exclusive
          size="small"
          value={snapVertexPriorityDistance}
          onChange={(_, val) => {
            if (val !== null) {
              setSnapVertexPriorityDistance(val);
              fireSnapOptions({ snapVertexPriorityDistance: val });
            }
          }}
          sx={{ mt: 0.5, mb: 1 }}
          fullWidth
        >
          {vertexPriorityMarks.map((m) => (
            <ToggleButton key={m.label} value={m.value} sx={{ py: 0.25, fontSize: 11 }}>
              {m.label}
            </ToggleButton>
          ))}
        </ToggleButtonGroup>

        {/* snapToMidPoints */}
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mt: 0.5 }}>
          <Typography variant="caption" color="text.secondary">
            Snap to midpoints
          </Typography>
          <Switch
            size="small"
            checked={snapToMidPoints}
            onChange={(_, checked) => {
              setSnapToMidPoints(checked);
              fireSnapOptions({ snapToMidPoints: checked });
            }}
          />
        </Box>
      </Popover>
    </Box>
  );
};

export default SnappingButton;
